import { type FC, useState } from "react";
import { Form } from "react-bootstrap";
import {
	MetaAdsBidStrategy,
	MetaAdsBudgetType,
	MetaAdsCampaignObjective,
	type MetaAdsCampaignTemplate,
	type MetaAdsCampaignTemplateSettings,
	MetaAdsConversionLocation,
	type MetaAdsCreateCampaignTemplateDto,
	MetaAdsCTA,
	MetaAdsOptimizationGoal,
} from "shared";
import { Modal, useModal } from "../../common/modal/Modal";
import { Select } from "../../components/Select";
import { centsToDollars, dollarsToCents } from "../metaAdsMoney";

const OBJECTIVE_OPTIONS: { value: MetaAdsCampaignObjective; label: string }[] =
	[
		{ value: MetaAdsCampaignObjective.OUTCOME_AWARENESS, label: "Awareness" },
		{ value: MetaAdsCampaignObjective.OUTCOME_TRAFFIC, label: "Traffic" },
		{
			value: MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT,
			label: "Engagement",
		},
		{ value: MetaAdsCampaignObjective.OUTCOME_LEADS, label: "Leads" },
		{ value: MetaAdsCampaignObjective.OUTCOME_SALES, label: "Sales" },
	];

const OPTIMIZATION_OPTIONS: {
	value: MetaAdsOptimizationGoal;
	label: string;
}[] = [
	{ value: MetaAdsOptimizationGoal.LINK_CLICKS, label: "Link Clicks" },
	{
		value: MetaAdsOptimizationGoal.LANDING_PAGE_VIEWS,
		label: "Landing Page Views",
	},
	{
		value: MetaAdsOptimizationGoal.LEAD_GENERATION,
		label: "Lead Generation",
	},
	{ value: MetaAdsOptimizationGoal.REACH, label: "Reach" },
	{ value: MetaAdsOptimizationGoal.AD_RECALL_LIFT, label: "Ad Recall Lift" },
	{
		value: MetaAdsOptimizationGoal.POST_ENGAGEMENT,
		label: "Post Engagement",
	},
	{ value: MetaAdsOptimizationGoal.THRUPLAY, label: "ThruPlay" },
	{ value: MetaAdsOptimizationGoal.CONVERSATIONS, label: "Conversations" },
	{ value: MetaAdsOptimizationGoal.PAGE_LIKES, label: "Page Likes" },
	{
		value: MetaAdsOptimizationGoal.OFFSITE_CONVERSIONS,
		label: "Offsite Conversions",
	},
	{ value: MetaAdsOptimizationGoal.VALUE, label: "Value" },
];

const BID_STRATEGY_OPTIONS: {
	value: MetaAdsBidStrategy;
	label: string;
}[] = [
	{
		value: MetaAdsBidStrategy.LOWEST_COST_WITHOUT_CAP,
		label: "Lowest Cost (No Cap)",
	},
	{
		value: MetaAdsBidStrategy.LOWEST_COST_WITH_BID_CAP,
		label: "Lowest Cost (Bid Cap)",
	},
	{ value: MetaAdsBidStrategy.COST_CAP, label: "Cost Cap" },
];

const CTA_OPTIONS: { value: MetaAdsCTA; label: string }[] = [
	{ value: MetaAdsCTA.LEARN_MORE, label: "Learn More" },
	{ value: MetaAdsCTA.SHOP_NOW, label: "Shop Now" },
	{ value: MetaAdsCTA.SIGN_UP, label: "Sign Up" },
	{ value: MetaAdsCTA.CONTACT_US, label: "Contact Us" },
	{ value: MetaAdsCTA.GET_QUOTE, label: "Get Quote" },
	{ value: MetaAdsCTA.BOOK_NOW, label: "Book Now" },
	{ value: MetaAdsCTA.SEND_MESSAGE, label: "Send Message" },
	{ value: MetaAdsCTA.LIKE_PAGE, label: "Like Page" },
	{ value: MetaAdsCTA.CALL_NOW, label: "Call Now" },
	{ value: MetaAdsCTA.BUY_NOW, label: "Buy Now" },
	{ value: MetaAdsCTA.ORDER_NOW, label: "Order Now" },
];

const CONVERSION_LOCATION_OPTIONS: {
	value: MetaAdsConversionLocation;
	label: string;
}[] = [
	{ value: MetaAdsConversionLocation.INSTANT_FORM, label: "Instant Form" },
	{ value: MetaAdsConversionLocation.WEBSITE, label: "Website" },
	{ value: MetaAdsConversionLocation.MESSENGER, label: "Messenger" },
	{ value: MetaAdsConversionLocation.ON_POST, label: "On Post" },
	{ value: MetaAdsConversionLocation.ON_PAGE, label: "On Page" },
	{ value: MetaAdsConversionLocation.CALLS, label: "Calls" },
	{
		value: MetaAdsConversionLocation.INSTAGRAM_DIRECT,
		label: "Instagram Direct",
	},
];

function defaultSettings(): MetaAdsCampaignTemplateSettings {
	return {
		campaignBudgetOptimization: false,
		budgetType: MetaAdsBudgetType.DAILY,
		budgetAmount: 2000,
		placements: "automatic",
		ageMin: 18,
		ageMax: 65,
		genders: [0],
		optimizationGoal: MetaAdsOptimizationGoal.LINK_CLICKS,
		bidStrategy: MetaAdsBidStrategy.LOWEST_COST_WITHOUT_CAP,
		bidAmount: null,
		callToAction: MetaAdsCTA.LEARN_MORE,
		conversionLocation: MetaAdsConversionLocation.WEBSITE,
	};
}

interface ModalCampaignTemplateProps {
	onSave: (dto: MetaAdsCreateCampaignTemplateDto) => Promise<void>;
	initial?: MetaAdsCampaignTemplate;
}

export const ModalCampaignTemplate: FC<ModalCampaignTemplateProps> = ({
	onSave,
	initial,
}) => {
	const { close } = useModal();
	const [name, setName] = useState(initial?.name ?? "");
	const [description, setDescription] = useState(initial?.description ?? "");
	const [objective, setObjective] = useState<MetaAdsCampaignObjective>(
		initial?.objective ?? MetaAdsCampaignObjective.OUTCOME_TRAFFIC,
	);
	const [settings, setSettings] = useState<MetaAdsCampaignTemplateSettings>(
		initial?.settings ?? defaultSettings(),
	);
	const [submitting, setSubmitting] = useState(false);

	const updateSettings = (
		partial: Partial<MetaAdsCampaignTemplateSettings>,
	) => {
		setSettings((prev) => ({ ...prev, ...partial }));
	};

	const handleSubmit = async () => {
		if (!name.trim()) return;
		setSubmitting(true);
		try {
			await onSave({ name, description, objective, settings });
			close();
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal size="lg">
			<h5>{initial ? "Edit Campaign Template" : "Create Campaign Template"}</h5>

			<Form.Group className="mb-3">
				<Form.Label>Template Name</Form.Label>
				<Form.Control
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="e.g. Holiday Traffic Campaign"
				/>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>Description</Form.Label>
				<Form.Control
					as="textarea"
					rows={2}
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					placeholder="Brief description"
				/>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>Objective</Form.Label>
				<Select
					value={objective}
					onChange={(e) =>
						setObjective(e.target.value as MetaAdsCampaignObjective)
					}
					disabled={!!initial}
				>
					{OBJECTIVE_OPTIONS.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</Select>
			</Form.Group>

			<hr />
			<h6>Default Settings</h6>

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Budget Type</Form.Label>
						<Select
							value={settings.budgetType}
							onChange={(e) =>
								updateSettings({
									budgetType: e.target.value as MetaAdsBudgetType,
								})
							}
						>
							<option value={MetaAdsBudgetType.DAILY}>Daily</option>
							<option value={MetaAdsBudgetType.LIFETIME}>Lifetime</option>
						</Select>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Budget Amount</Form.Label>
						<div className="input-group">
							<span className="input-group-text">$</span>
							<Form.Control
								type="number"
								min={0}
								step={0.01}
								value={centsToDollars(settings.budgetAmount)}
								onChange={(e) =>
									updateSettings({
										budgetAmount: dollarsToCents(Number(e.target.value)),
									})
								}
							/>
						</div>
					</Form.Group>
				</div>
			</div>

			<Form.Group className="mb-3">
				<Form.Check
					type="switch"
					label="Campaign Budget Optimization (CBO)"
					checked={settings.campaignBudgetOptimization}
					onChange={(e) =>
						updateSettings({
							campaignBudgetOptimization: e.target.checked,
						})
					}
				/>
			</Form.Group>

			<div className="row">
				<div className="col-md-4">
					<Form.Group className="mb-3">
						<Form.Label>Age Min</Form.Label>
						<Form.Control
							type="number"
							min={13}
							max={65}
							value={settings.ageMin}
							onChange={(e) =>
								updateSettings({ ageMin: Number(e.target.value) })
							}
						/>
					</Form.Group>
				</div>
				<div className="col-md-4">
					<Form.Group className="mb-3">
						<Form.Label>Age Max</Form.Label>
						<Form.Control
							type="number"
							min={13}
							max={65}
							value={settings.ageMax}
							onChange={(e) =>
								updateSettings({ ageMax: Number(e.target.value) })
							}
						/>
					</Form.Group>
				</div>
				<div className="col-md-4">
					<Form.Group className="mb-3">
						<Form.Label>Gender</Form.Label>
						<Select
							value={settings.genders[0]}
							onChange={(e) =>
								updateSettings({ genders: [Number(e.target.value)] })
							}
						>
							<option value={0}>All</option>
							<option value={1}>Male</option>
							<option value={2}>Female</option>
						</Select>
					</Form.Group>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Optimization Goal</Form.Label>
						<Select
							value={settings.optimizationGoal}
							onChange={(e) =>
								updateSettings({
									optimizationGoal: e.target.value as MetaAdsOptimizationGoal,
								})
							}
						>
							{OPTIMIZATION_OPTIONS.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</Select>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Bid Strategy</Form.Label>
						<Select
							value={settings.bidStrategy}
							onChange={(e) =>
								updateSettings({
									bidStrategy: e.target.value as MetaAdsBidStrategy,
								})
							}
						>
							{BID_STRATEGY_OPTIONS.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</Select>
					</Form.Group>
				</div>
			</div>

			{settings.bidStrategy !== MetaAdsBidStrategy.LOWEST_COST_WITHOUT_CAP && (
				<Form.Group className="mb-3">
					<Form.Label>Bid Amount</Form.Label>
					<div className="input-group">
						<span className="input-group-text">$</span>
						<Form.Control
							type="number"
							min={0}
							step={0.01}
							value={
								settings.bidAmount != null
									? centsToDollars(settings.bidAmount)
									: ""
							}
							onChange={(e) =>
								updateSettings({
									bidAmount: e.target.value
										? dollarsToCents(Number(e.target.value))
										: null,
								})
							}
						/>
					</div>
				</Form.Group>
			)}

			<div className="row">
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Call to Action</Form.Label>
						<Select
							value={settings.callToAction}
							onChange={(e) =>
								updateSettings({
									callToAction: e.target.value as MetaAdsCTA,
								})
							}
						>
							{CTA_OPTIONS.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</Select>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="mb-3">
						<Form.Label>Conversion Location</Form.Label>
						<Select
							value={settings.conversionLocation}
							onChange={(e) =>
								updateSettings({
									conversionLocation: e.target
										.value as MetaAdsConversionLocation,
								})
							}
						>
							{CONVERSION_LOCATION_OPTIONS.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</Select>
					</Form.Group>
				</div>
			</div>

			<div className="d-flex gap-2">
				<button
					type="button"
					className="btn btn-primary flex-grow-1"
					disabled={submitting || !name.trim()}
					onClick={handleSubmit}
				>
					{submitting ? "Saving..." : "Save"}
				</button>
				<button
					type="button"
					className="btn btn-outline-secondary"
					onClick={close}
				>
					Cancel
				</button>
			</div>
		</Modal>
	);
};
